import { useEffect, useState } from "react";
import { X, Shirt, PackageOpen } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { api, apiError } from "@/lib/helpers";
import { toast } from "sonner";

const money = (n) => `$${Number(n).toFixed(2).replace(/\.00$/, "")}`;

export default function PrintfulShop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/printful/products");
        setProducts(Array.isArray(data) ? data : []);
      } catch (e) {
        toast.error(apiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <section id="merch" data-testid="printful-shop" className="relative bg-sand py-20 sm:py-24 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <p className="eyebrow">Wag n Tails Boutique</p>
          <h2 className="mt-3 font-serif text-2xl sm:text-3xl lg:text-4xl text-charcoal tracking-tight leading-snug">
            Straight from our catalog.
          </h2>
          <p className="mt-4 text-bark leading-relaxed">
            Every item is printed on demand and shipped to your door — proceeds go straight back into food, vet bills and cozy beds.
          </p>
        </Reveal>

        {loading ? (
          <div data-testid="printful-shop-loading" className="mt-12 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="aspect-square animate-pulse rounded-3xl bg-stone-200/70" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div data-testid="printful-shop-empty" className="mt-12 rounded-3xl border border-stone-200 bg-white p-10 text-center">
            <PackageOpen className="mx-auto h-10 w-10 text-gold-deep" />
            <p className="mt-4 font-serif text-xl text-charcoal">The shop is being stocked.</p>
            <p className="mt-2 text-sm text-bark">Fresh designs are syncing from our print partner — check back shortly.</p>
          </div>
        ) : (
          <div className="mt-12 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
            {products.map((p, i) => {
              const prices = (p.variants || []).map((v) => Number(v.price)).filter((n) => n > 0);
              const from = prices.length ? Math.min(...prices) : 0;
              return (
                <Reveal key={p.printful_product_id} delay={Math.min(i * 0.04, 0.3)}>
                  <button
                    data-testid={`printful-product-${p.printful_product_id}`}
                    onClick={() => setDetail(p)}
                    className="group w-full overflow-hidden rounded-3xl border border-stone-200 bg-white text-left transition-all hover:-translate-y-1 hover:shadow-lg"
                  >
                    <div className="aspect-square overflow-hidden bg-stone-100">
                      {p.image ? (
                        <img src={p.image} alt={p.name} loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center">
                          <Shirt className="h-10 w-10 text-stone-300" />
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="truncate font-serif text-base text-charcoal">{p.name}</h3>
                      <p className="mt-1 font-mono text-sm font-bold text-pine">{from > 0 ? `From ${money(from)}` : "Priced at checkout"}</p>
                    </div>
                  </button>
                </Reveal>
              );
            })}
          </div>
        )}
      </div>

      {detail && (
        <div data-testid="printful-detail" className="fixed inset-0 z-50 flex items-end justify-center bg-charcoal/60 p-0 sm:items-center sm:p-6"
          onClick={() => setDetail(null)}>
          <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-6 sm:rounded-3xl"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-serif text-2xl text-charcoal">{detail.name}</h3>
                <p className="mt-1 text-sm text-bark">{detail.variants?.length || 0} options available</p>
              </div>
              <button data-testid="printful-detail-close" onClick={() => setDetail(null)}
                className="rounded-full bg-stone-100 p-2 text-charcoal hover:bg-stone-200 transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>
            {detail.image && (
              <img src={detail.image} alt={detail.name} className="mt-4 aspect-video w-full rounded-2xl object-cover" />
            )}
            <div className="mt-4 max-h-64 overflow-y-auto rounded-2xl border border-stone-200">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-left text-xs uppercase tracking-wide text-bark">
                  <tr><th className="px-4 py-2">Color</th><th className="px-4 py-2">Size</th><th className="px-4 py-2 text-right">Price</th></tr>
                </thead>
                <tbody>
                  {(detail.variants || []).map((v) => (
                    <tr key={v.id} data-testid={`printful-variant-${v.printful_variant_id}`} className="border-t border-stone-100">
                      <td className="px-4 py-2 text-charcoal">{v.color || "—"}</td>
                      <td className="px-4 py-2 text-charcoal">{v.size || "—"}</td>
                      <td className="px-4 py-2 text-right font-mono font-bold text-pine">{money(v.price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-xs text-bark">Checkout opens when the shop fully launches — thanks for browsing!</p>
          </div>
        </div>
      )}
    </section>
  );
}
